import { Alert, Flex, Label, Text } from "@gravity-ui/uikit";
import { ProductDetailResponseDto } from "definitions/product-storage";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ImageSlider } from "src/components";
import { useGravityDevice } from "src/hooks/useGravityDevice";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { buildImageUrl } from "src/util";

import { useProductModalContentTranslate } from "./hooks";
import css from "./ProductModalContent.module.scss";

export const ProductModalContent = ({
  product,
  hasMoney,
  hasSubscribe,
}: {
  product: ProductDetailResponseDto;
  hasMoney?: boolean;
  hasSubscribe?: boolean;
}) => {
  const navigate = useNavigate();
  const translations = useProductModalContentTranslate();

  const handleOnClick = useCallback(() => {
    navigate(RoutesPaths.LK.SUBSCRIBE);
  }, [navigate]);

  const { isMobile } = useGravityDevice();

  const imageSliders = useMemo(
    () =>
      product?.productImages?.map((image) =>
        buildImageUrl(image.image, {
          width: 1200,
          height: 1200,
          format: "webp",
        }),
      ) ?? [],
    [product?.productImages],
  );

  return (
    <>
      <Flex
        direction={isMobile ? "column" : "row"}
        className={isMobile && css.contentMargin}
        gap="10"
        justifyContent={"center"}
      >
        <Flex justifyContent={"center"}>
          <ImageSlider images={imageSliders} />
        </Flex>
        <Flex direction="column" gap="5" className={css.content}>
          <Text variant="subheader-3">{product.title}</Text>
          <Flex gap="1" wrap="wrap">
            <Label
              type={"copy"}
              interactive={true}
              value={product.article}
              copyText={product.article}
            >
              {translations.article}
            </Label>
            <Label
              type={"copy"}
              interactive={true}
              value={product.id.toString()}
              copyText={product.id.toString()}
            >
              {translations.productId}
            </Label>
          </Flex>

          {product.description && (
            <Text variant="body-2" color="secondary">
              {product.description}
            </Text>
          )}
          {product.productSources.length > 0 && (
            <Flex gap="1" wrap="wrap">
              <Text variant={"body-2"}>ID внешних систем:</Text>
              {product.productSources.map((source) => (
                <Label
                  key={source.externalId}
                  type={"copy"}
                  interactive={true}
                  value={source.externalId}
                  copyText={source.externalId}
                >
                  {translations.productId} {source.source}
                </Label>
              ))}
            </Flex>
          )}

          {product.brand && (
            <Flex gap="1" wrap="wrap">
              <Label type="copy" value={product.brand.name}>
                {translations.brand}
              </Label>
            </Flex>
          )}

          {product.category && (
            <Flex gap="1" wrap="wrap">
              <Label type="copy" value={product.category.name}>
                {translations.category}
              </Label>
            </Flex>
          )}

          <Flex direction="row" wrap="wrap" gap="3" className={css.buyButtons}>
            {hasMoney || hasSubscribe ? (
              <Flex direction="column" gap="2">
                <Text variant="body-2">
                  {translations.soldCount}: {product.soldCount}
                </Text>
                <Text variant="body-2">
                  {translations.favoriteCount}: {product.favoriteCount}
                </Text>
                <Text variant="body-2">
                  {translations.gender}: {product.gender}
                </Text>
                <Text variant="body-2">
                  {translations.source}: {product.mainSource}
                </Text>
              </Flex>
            ) : (
              <Alert
                title={translations.noSubscriptionAlert}
                theme="warning"
                actions={[
                  {
                    text: translations.buySubscription,
                    handler: handleOnClick,
                  },
                ]}
                layout={isMobile ? "vertical" : "horizontal"}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
